'use client';

/**
 * MessageReactionRow — the per-message reaction row under each of Sara's
 * answers (message-block.tsx's bare-prose assistant branch — see that
 * file's comment on where this attaches). Container half of the pure-view-
 * machine split: lib/message-reaction-view.ts owns the sentiment/panel
 * transition table (unit-tested there); this component owns the one live
 * API call (lib/api/feedback.ts) and the panels' own draft text, following
 * the exact precedent of handoff-timeout-panel.tsx / -message.tsx.
 *
 * "Resting until hovered" (source doc): visible by default (the safe,
 * touch-reachable state), then hidden-until-hover ONLY on pointers that can
 * actually hover — gated behind `(hover: hover) and (pointer: fine)` so a
 * phone (no hover concept) never gets a thumb it can't reach. `:focus-within`
 * always reveals it regardless of pointer type, so Tab can still reach every
 * button even while it's visually resting — the buttons stay in normal tab
 * order the whole time (opacity, not `hidden`/`display:none`, is what hides
 * them), so focus arrives before visibility does, not after.
 *
 * One tap, done: tapping 👍 (or 👎) fires the API call immediately — the
 * REASON panel that opens for 👎 is a pure enrichment layered on top, never
 * a gate the down-vote waits on (see message-reaction-view.ts's header for
 * the full rationale). Tapping an already-active thumb again retracts it —
 * modeled as sending the SAME endpoint with `sentiment` omitted, since
 * that's the only way the wire shape (lib/api/feedback.ts) can express "no
 * sentiment" and a page reload must not resurrect a retracted thumb.
 *
 * What every submit() call actually sends: the backend's message-scope
 * endpoint is a full UPSERT of this message's ONE feedback row, not a merge
 * — so the payload must always carry the COMPLETE accumulated picture
 * (`selectedReasons`/`comment` state), never something derived from just
 * the panel that happens to be open (handleSend), and switching the thumb
 * to a DIFFERENT sentiment deliberately CLEARS that accumulated detail
 * first (handleToggleUp/handleToggleDown), the same as a retraction — detail
 * collected in service of one verdict doesn't carry over to a different
 * one. Both rules exist so the submitted payload and this component's local
 * state can never disagree about what's saved (see git history for the two
 * data-loss bugs this fixed: a switched thumb silently deleting a saved
 * note, and opening the note panel silently wiping saved reason chips).
 */
import { useRef, useState } from 'react';
import { Copy, Check, ThumbsUp, MessageSquareText } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  nextReactionState,
  ReactionAction,
  ReactionPanel,
  ReactionSentiment,
  type ReactionState,
} from '@/lib/message-reaction-view';
import { toggleSelection } from '@/lib/toggle-selection';
import {
  retractMessageFeedback,
  submitFeedback,
  FeedbackScope,
  FeedbackSentiment,
  FeedbackSource,
  type FeedbackResponse,
} from '@/lib/api/feedback';
import {
  MessageReactionPanel,
  MessageReactionPanelVariant,
} from './message-reaction-panel';

const GENERIC_ERROR = 'Something went wrong. Please try again.';

/** Copy-button "copied" glyph swap — long enough to register, short enough
 *  to not linger past the moment it's confirming. */
const COPIED_RESET_MS = 1400;

interface MessageReactionRowProps {
  conversationId: string;
  /** The server-side `messages.id` (Message.dbId in types/chat.ts) — the
   *  ONLY id `feedback.message_id` accepts. NOT the client-visible message
   *  id (a Realtime item_id, or a client-minted fallback) — submitting THAT
   *  is the bug the message-persisted id bridge fixes (see that field's doc
   *  comment): the backend can't parse an item_id as the UUID its FK
   *  expects, so a thumb on a live answer used to 422 and only "worked" on a
   *  restored row because a restored row's client id happens to already BE
   *  this same UUID.
   *
   *  Undefined until persistence is confirmed — restored rows have it from
   *  mount (restore-messages.ts stamps it explicitly); live rows get it when
   *  the backend's message_persisted frame lands, which can be a beat after
   *  the bubble itself appears. While undefined this row renders (never
   *  hidden — see the controls below) with its controls disabled. */
  dbId?: string;
  /** Sara's rendered answer text, for the Copy button. */
  text: string;
  /** Restored feedback for this message, resolved by the caller from
   *  GET .../feedback (see lib/feedback-restore.ts) — arrives async (a
   *  render or more after mount), so this is watched via effect rather than
   *  only read as an initial value. Undefined until restore resolves / if
   *  this message was never rated. */
  existingFeedback?: FeedbackResponse;
}

/** Shown as the disabled controls' `title` / accessible name while `dbId`
 *  is still unknown — explains the gap rather than leaving a silently dead
 *  button. */
const PENDING_PERSIST_TITLE =
  'Feedback becomes available once this answer is saved';

const BUTTON_BASE =
  'flex size-[32px] items-center justify-center rounded-[9px] text-[#a99e8c] transition-colors duration-150';

export function MessageReactionRow({
  conversationId,
  dbId,
  text,
  existingFeedback,
}: MessageReactionRowProps) {
  // Gates every submitting control below. Deliberately NOT a queued
  // pending-submission that flushes once dbId lands: that would need its own
  // little state machine (hold the tap, replay it, handle a tap-then-switch
  // while held, handle unmount mid-hold…) to buy back a window that's
  // typically under a second (the backend persists right after the turn
  // completes) on a control that's hover-revealed in the first place — see
  // this file's header for the "resting until hovered" behavior. Simpler and
  // just as correct: disable the controls until dbId exists, explain why via
  // title/aria-disabled, and let the user re-tap once it's ready.
  const feedbackPending = dbId == null;
  const [state, setState] = useState<ReactionState>({
    sentiment: null,
    panel: ReactionPanel.NONE,
  });
  const [selectedReasons, setSelectedReasons] = useState<string[]>([]);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Restore-from-GET is applied at most once, and only before the user has
  // touched this row themselves — the fetch resolving late (after the user
  // already tapped something) must never clobber a fresh interaction.
  const hasInteractedRef = useRef(false);
  const appliedRestoreRef = useRef(false);
  if (
    !appliedRestoreRef.current &&
    !hasInteractedRef.current &&
    existingFeedback
  ) {
    appliedRestoreRef.current = true;
    // Deliberately not wrapped in an effect: this only ever runs once, on
    // whichever render existingFeedback first arrives non-null, and setting
    // state here (before paint) avoids a flash of the unrated row. React
    // supports this "adjust state during render" pattern for exactly this
    // case (see https://react.dev/learn/you-might-not-need-an-effect).
    const restoredSentiment =
      existingFeedback.sentiment === FeedbackSentiment.UP
        ? ReactionSentiment.UP
        : existingFeedback.sentiment === FeedbackSentiment.DOWN
          ? ReactionSentiment.DOWN
          : null;
    setState({ sentiment: restoredSentiment, panel: ReactionPanel.NONE });
    setSelectedReasons(existingFeedback.reasons ?? []);
    setComment(existingFeedback.comment ?? '');
  }

  /** Returns whether the submit succeeded — callers that need to react to
   *  the outcome (handleSend closing the panel) check this return value
   *  directly rather than re-reading `error` state, which could still be
   *  showing a PRIOR failure's stale value at the instant this resolves. */
  const submit = async (
    sentiment: ReactionSentiment | null,
    reasons: string[],
    commentText: string
  ): Promise<boolean> => {
    if (!dbId) {
      // Belt-and-braces: every control that calls submit() is disabled
      // while dbId is absent (see feedbackPending below), so this should be
      // unreachable in practice. Bailing out here rather than posting with a
      // missing/undefined message_id keeps that invariant true even against
      // a stale closure or a future caller that forgets the guard.
      return false;
    }
    setSubmitting(true);
    setError(null);
    try {
      const trimmedComment = commentText.trim();
      if (sentiment === null && !trimmedComment) {
        // A bare retraction has no valid submission body — message scope
        // requires at least one of sentiment or comment — so undo is its
        // own verb. See retractMessageFeedback's doc comment.
        await retractMessageFeedback(conversationId, dbId);
        return true;
      }
      await submitFeedback(conversationId, {
        scope: FeedbackScope.MESSAGE,
        message_id: dbId,
        sentiment: sentiment ?? undefined,
        reasons,
        comment: trimmedComment || undefined,
        // Unprompted provenance — nobody opened an ask, the user just
        // reacted to an answer. See FeedbackSource.MESSAGE_ROW's doc
        // comment (feedback-contract.ts) for why this is a distinct value
        // from the four "we asked" triggers, and required (not optional)
        // since `source` is NOT NULL on the backend row.
        source: FeedbackSource.MESSAGE_ROW,
      });
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : GENERIC_ERROR);
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const dispatch = (action: ReactionAction) => nextReactionState(state, action);

  const handleToggleUp = () => {
    hasInteractedRef.current = true;
    const next = dispatch(ReactionAction.TOGGLE_UP);
    setState(next);
    if (next.sentiment === null) {
      // Retraction — clear any accumulated detail too, it no longer applies.
      setSelectedReasons([]);
      setComment('');
      void submit(null, [], '');
    } else {
      // Either a fresh 👍 tap OR a SWITCH away from 👎 (Defect 1). Chosen
      // behavior: a sentiment switch CLEARS any reasons/comment collected
      // under the OLD sentiment, the same as retraction just above — the
      // two are the same case from the data's point of view (the prior
      // judgment no longer holds, so neither does what the user said about
      // it). Concretely: "inaccurate" chips + a "the address was wrong" note
      // describe why the answer was bad; carrying them forward onto an UP
      // rating would submit a self-contradicting row and, worse, would
      // leave the server upsert (which just overwrote them to this UP's
      // empty payload) disagreeing with local state that still displays the
      // old text if the panel is reopened. Clearing both together up front
      // keeps the submitted payload and the local state in permanent
      // agreement, which is the actual bug this defect describes.
      setSelectedReasons([]);
      setComment('');
      void submit(ReactionSentiment.UP, [], '');
    }
  };

  const handleToggleDown = () => {
    hasInteractedRef.current = true;
    const next = dispatch(ReactionAction.TOGGLE_DOWN);
    setState(next);
    if (next.sentiment === null) {
      setSelectedReasons([]);
      setComment('');
      void submit(null, [], '');
    } else {
      // Symmetric with handleToggleUp above: a switch INTO down (from up, or
      // from no sentiment) clears whatever detail was collected under the
      // OLD sentiment first. In practice this only matters when a note was
      // attached while UP was active (OPEN_NOTE works regardless of
      // sentiment — see message-reaction-view.ts) — but keeping both
      // toggles clear-on-switch identically means this doesn't silently
      // regress the moment that becomes possible. The down-vote itself is
      // still already "sent" here — the REASON panel that just opened is
      // optional enrichment on top, per this file's header.
      setSelectedReasons([]);
      setComment('');
      void submit(ReactionSentiment.DOWN, [], '');
    }
  };

  const handleNoteButton = () => {
    hasInteractedRef.current = true;
    setState(
      dispatch(
        state.panel === ReactionPanel.NOTE
          ? ReactionAction.CLOSE_PANEL
          : ReactionAction.OPEN_NOTE
      )
    );
  };

  const handleSkipOrCancel = () => {
    setError(null);
    setState(dispatch(ReactionAction.CLOSE_PANEL));
  };

  const handleSend = async () => {
    hasInteractedRef.current = true;
    // Always submit the FULL accumulated `selectedReasons`/`comment` —
    // never derive what to send from which panel happens to be open
    // (Defect 2). The backend's message-scope endpoint is an UPSERT (a
    // full replace of this message's feedback row, not a merge), so
    // submitting anything less than everything the user has accumulated so
    // far silently deletes the rest: opening the NOTE panel used to send
    // `reasons: []` here regardless of chips already saved from an earlier
    // REASON-panel Send, wiping them. selectedReasons/comment already ARE
    // the complete accumulated picture regardless of which panel is
    // currently open — REASON is the only place chips get edited, NOTE is
    // the only place the free-text note gets edited, but both pieces of
    // state persist across panel switches (same assumption the
    // GET-restore path above already makes), so there is no "panel-shape"
    // to gate on here.
    const ok = await submit(state.sentiment, selectedReasons, comment);
    // Only close on success — a failed Send leaves the panel (and the
    // user's typed reasons/comment) exactly as they were, per this task's
    // "submit failures must preserve what the user typed" requirement.
    if (ok) {
      setState((prev) => ({ ...prev, panel: ReactionPanel.NONE }));
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), COPIED_RESET_MS);
    } catch {
      // Clipboard permission denied/unavailable — a quiet no-op is the
      // right failure mode for a convenience action like this, not an
      // inline error banner under a message.
    }
  };

  const isUp = state.sentiment === ReactionSentiment.UP;
  const isDown = state.sentiment === ReactionSentiment.DOWN;
  const noteOpen = state.panel === ReactionPanel.NOTE;

  return (
    <div className="flex flex-col gap-[6px]">
      <div
        className={cn(
          // Optically left-align the first ICON with the prose above it, not
          // the button's box: each control is a 32px hit-area around a 16px
          // glyph, so the glyph sits 8px inside its own button. Without the
          // pull the row reads as indented under the answer.
          'flex items-center gap-[2px] -ml-[8px]',
          'opacity-100 transition-opacity duration-150',
          // Hide-until-hover ONLY where hover is a real pointer affordance —
          // see this file's header for why touch stays permanently visible.
          '[@media(hover:hover)_and_(pointer:fine)]:opacity-0',
          '[@media(hover:hover)_and_(pointer:fine)]:focus-within:opacity-100',
          '[@media(hover:hover)_and_(pointer:fine)]:group-hover:opacity-100'
        )}
      >
        <button
          type="button"
          title="Copy"
          aria-label="Copy"
          onClick={handleCopy}
          className={cn(BUTTON_BASE, 'hover:bg-[#f3ede3] hover:text-[#E36001]')}
        >
          {copied ? (
            <Check aria-hidden="true" size={16} strokeWidth={2} />
          ) : (
            <Copy aria-hidden="true" size={16} strokeWidth={2} />
          )}
        </button>

        <button
          type="button"
          title={feedbackPending ? PENDING_PERSIST_TITLE : 'Good answer'}
          aria-label="Good answer"
          aria-pressed={isUp}
          aria-disabled={feedbackPending}
          onClick={feedbackPending ? undefined : handleToggleUp}
          className={cn(
            BUTTON_BASE,
            feedbackPending
              ? 'cursor-not-allowed opacity-40'
              : isUp
                ? 'bg-[#eef6f0] text-[#1F8A5B]'
                : 'hover:bg-[#eef6f0] hover:text-[#1F8A5B]'
          )}
        >
          <ThumbsUp
            aria-hidden="true"
            size={16}
            strokeWidth={2}
            fill={isUp ? 'currentColor' : 'none'}
          />
        </button>

        <button
          type="button"
          title={feedbackPending ? PENDING_PERSIST_TITLE : 'Needs work'}
          aria-label="Needs work"
          aria-pressed={isDown}
          aria-disabled={feedbackPending}
          onClick={feedbackPending ? undefined : handleToggleDown}
          className={cn(
            BUTTON_BASE,
            feedbackPending
              ? 'cursor-not-allowed opacity-40'
              : isDown
                ? 'bg-[#faece4] text-[#E36001]'
                : 'hover:bg-[#faece4] hover:text-[#E36001]'
          )}
        >
          {/* Thumb-down is the thumb-up glyph rotated 180° — not a separate
              icon (per this task's brief). */}
          <ThumbsUp
            aria-hidden="true"
            size={16}
            strokeWidth={2}
            className="rotate-180"
            fill={isDown ? 'currentColor' : 'none'}
          />
        </button>

        <div
          className="mx-[4px] h-[18px] w-[1px] bg-[#eadfce]"
          aria-hidden="true"
        />

        <button
          type="button"
          title={feedbackPending ? PENDING_PERSIST_TITLE : 'Add a note'}
          aria-label="Add a note"
          aria-pressed={noteOpen}
          aria-disabled={feedbackPending}
          onClick={feedbackPending ? undefined : handleNoteButton}
          className={cn(
            BUTTON_BASE,
            feedbackPending
              ? 'cursor-not-allowed opacity-40'
              : noteOpen
                ? 'bg-[#eef2f8] text-[#31364E]'
                : 'hover:bg-[#f3ede3] hover:text-[#31364E]'
          )}
        >
          <MessageSquareText
            aria-hidden="true"
            size={16}
            strokeWidth={2}
            fill={noteOpen ? 'currentColor' : 'none'}
          />
        </button>
      </div>

      {state.panel !== ReactionPanel.NONE && (
        <MessageReactionPanel
          variant={
            state.panel === ReactionPanel.REASON
              ? MessageReactionPanelVariant.REASON
              : MessageReactionPanelVariant.NOTE
          }
          selectedReasons={selectedReasons}
          onToggleReason={(reason) => {
            hasInteractedRef.current = true;
            setSelectedReasons((prev) => toggleSelection(prev, reason));
          }}
          comment={comment}
          onCommentChange={(value) => {
            hasInteractedRef.current = true;
            setComment(value);
          }}
          onSkip={handleSkipOrCancel}
          onSend={handleSend}
          submitting={submitting}
          error={error}
        />
      )}
    </div>
  );
}
